import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaFutbol, FaCalendarAlt, FaCheck, FaPlus, FaComments } from "react-icons/fa";
import HomeShell from "../components/HomeShell";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/api";
import { messaggioDelServer } from "../utils/enrolDati";
// La lettura degli id dei figli ha già una sede: `idsDeiFigli` (utils/famiglia.js),
// che leggono anche HomeParent e HomeIncomplete. Non se ne scrive una seconda copia.
import { idsDeiFigli } from "../utils/famiglia";
// Il filtro di RUOLO di chi si può iscrivere (PLAYER e COACH, dal 15/9) vive
// in ClinicEvents.jsx, unico per le due porte interne: qui si importa, non si
// riscrive. (Le date dell'evento qui non si mostrano più: v. la lista, sotto.)
import { eIscrivibileAllaClinic } from "./ClinicEvents";
import { normalizeRole } from "../auth/access";
// Campi e pulsanti di sistema (doc 07 §5.1 oro, §5.3 fantasma): si importa il
// foglio, non si ridisegna nulla. Il layout è quello della plancia.
import "../components/AdminTools.css";
import "./ClinicEvents.css";

/**
 * CLINIC SESSIONS — l'area del SOCIO (cantiere CLINICS, Fetta C2, 15/9/2026).
 * Base di verità: DISEGNO v2 §5 («le 3 forme»), §6 (agisce il tutore).
 *
 * Da qui un genitore del club vede gli eventi clinic APERTI e iscrive un
 * proprio figlio, uno alla volta. Null'altro: non crea eventi, non vede
 * chi altro si è iscritto, non tocca i gruppi. Quella è la plancia
 * dell'amministrazione (/clinic-events), ed è un'altra pagina con un altro
 * cancello.
 *
 * ⚠️ PER RUOLO, NON PER ETÀ (decisione del committente del 15/9): si offre
 * un profilo collegato se è PLAYER o COACH (`eIscrivibileAllaClinic`, la stessa
 * regola della plancia), adulto o minore che sia. Il vincolo «solo minori» è
 * caduto sia qui sia sul server, che decide comunque da solo sul documento
 * riletto (400 «Only players and coaches…»).
 *
 * ⚠️ UN ADULTO NON ISCRIVE SÉ STESSO (decisione del committente del 15/9). La
 * pagina non offre il gesto, e il server lo rifiuta comunque: `eSeStessoIlBersaglio`
 * in POST /clinic-member/events/:id/enrol intercetta il caso prima della tutela,
 * perché `eProprietarioOTutore` su sé stessi risponde `true`.
 *
 * ⚠️ LA VIA DEI DATI È QUELLA DI HomeParent, e non un'altra: gli id dei figli da
 * `linkedProfiles` (già dentro `user` di AuthContext, nessuna chiamata) e una
 * `GET /users/:id` per figlio, in parallelo. `GET /users` — l'anagrafica intera —
 * è riservata ad ADMIN/SUPERADMIN e a un genitore risponde 403.
 *
 * ⚠️ IL SUGGERIMENTO NON È IL PERMESSO. `enrolledChildIds`, che il server manda
 * con ogni evento, serve a mostrare «già iscritto» e a spegnere il pulsante: è
 * costruito su `linkedProfiles`, la faccia DEBOLE del legame. Il permesso vero
 * lo decide la rotta d'iscrizione su `guardians` del FIGLIO, e un figlio che
 * comparisse qui senza quel legame riceverebbe 403. Allo stesso modo l'evento
 * è riletto dal server a ogni invio: chiuso fra la lettura e il clic è un 409,
 * non un'iscrizione.
 *
 * ⚠️ «IO» È UNO STATO, NON UN PULSANTE (CLINICS lato utente §3, 23/9). Un
 * PLAYER/COACH adulto può essere iscritto dall'amministrazione: il server lo
 * dice in `selfEnrolled` (a parte da `enrolledChildIds`, che accende «Enrol»)
 * e la pagina mostra la riga «You · Enrolled / Not enrolled». Il gruppo arriva
 * già risolto per nome in `enrolledGroups` ({ idPersona: nome | "" }), solo per
 * i propri iscritti: i gruppi interi dell'evento al socio non arrivano.
 *
 * ⚠️ IL CALENDARIO È UN'ALTRA PAGINA (CALENDARIO CLINIC, Fetta 2, 23/9). Ogni
 * riga ENROLLED porta un bottone «Calendar» verso /clinics/calendar/:personId
 * (ClinicCalendar.jsx), una persona alla volta. Qui non si legge nessuna
 * sessione: il permesso di vederle lo decide la rotta del calendario sul
 * server, con la tutela forte (`guardians`), non questa pagina.
 *
 * ⚙️ LA VISTA DEL COACH («BOARD - CHAT» Fetta C, 26/9/2026, BAC-22). Per gli
 * eventi in cui il server riconosce il chiamante come COACH DELL'EVENTO, la
 * risposta porta `coachGroups` ([{ id, name }], tutti i gruppi dell'evento):
 * sotto le proprie righe compare l'elenco dei gruppi, ognuno col suo
 * «BOARD - CHAT» verso /clinics/board/:eventId/:propriaScheda?group=:groupId.
 * Senza `coachGroups` nulla cambia. Chi sia coach lo decide il server.
 *
 * ⚠️ IL CONFINE. Nessuna stagione, nessuna squadra, nessuna tessera: le clinic
 * sono cross-stagione (DISEGNO §1). La pagina non monta la barra della stagione
 * e non nomina annate — non è una svista.
 *
 * Il controllo d'accesso NON sta qui: sta in App.jsx, nel cancello condiviso
 * RequireRole, con lo stesso elenco di ruoli di /more — da dove ci si arriva.
 * Il GUEST è fuori da /clinics per costruzione; dalla G2 vede questa stessa
 * pagina su /home/guest (qui sotto), e il server gli apre le rotte
 * /clinic-member/*.
 *
 * Testi a schermo in inglese (CLAUDE.md §3.7), «enrol» e non «register».
 *
 * ⚠️ È ANCHE LA PAGINA DEL GUEST (Fetta G2, 24/9/2026). HomeGuest la monta con
 * `home` (nessun «Back»: è la sua home, e dietro non c'è nulla). Le righe sono
 * quelle di sempre, con due aggiunte per gli OSPITI (stessa regola del server,
 * `eOspiteIscrivibileAllaClinic` in server.js):
 *   · il GENITORE ospite vede i suoi bambini ospiti (via `linkedProfiles`, come
 *     un PARENT socio) con «Enrol», e nessuna riga «You»;
 *   · l'ADULTO ospite vede la riga «You» CON «Enrol»: è l'unica eccezione al
 *     «non sé stessi», e il server la ammette solo a lui.
 * Per i soci la pagina è quella di prima.
 */

// L'OSPITE ISCRIVIBILE — calco dichiarato di `eOspiteAdultoClinic` /
// `eOspiteMinoreClinic` (server.js, G2). Qui decide solo che cosa si MOSTRA:
// il permesso lo decide il server sul documento riletto.
const JOINING_AS = ["PLAYER", "COACH"];

function eOspiteSenzaTessera(d) {
  return (
    Boolean(d) &&
    normalizeRole(d.role) === "GUEST" &&
    !(Array.isArray(d.contexts) && d.contexts.length > 0)
  );
}

function haTutori(d) {
  return (
    Array.isArray(d.guardians) &&
    d.guardians.some((voce) => typeof voce === "string" && voce.trim())
  );
}

function eOspiteAdulto(d) {
  return (
    eOspiteSenzaTessera(d) &&
    d.isMinor !== true &&
    !haTutori(d) &&
    JOINING_AS.includes(d.clinicJoiningAs)
  );
}

function eOspiteMinore(d) {
  return eOspiteSenzaTessera(d) && d.isMinor === true && haTutori(d);
}

// Che cosa dire quando un gesto non riesce. Stessa distinzione della plancia
// (spiegazioneDiUnErrore in ClinicEvents.jsx) e per la stessa ragione: se il
// server ha risposto, il motivo sta nel corpo — ed è lì che vivono i «no»
// parlanti di questa rotta («This person is already enrolled…»). Se risposta non
// ce n'è stata, la colpa non è di ciò che l'utente ha premuto.
function spiegazioneDiUnErrore(err) {
  const dalServer = messaggioDelServer(err);

  if (dalServer) return dalServer;

  return err && err.status
    ? "The club could not accept the enrolment. Nothing was saved."
    : "The club server could not be reached. Nothing was saved.";
}

function nomeDi(documento) {
  return [documento && documento.firstName, documento && documento.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
}

// «ENROLLED · Group B» / «ENROLLED · no group yet». Il nome arriva dal server
// già risolto (`enrolledGroups`); assente o vuoto = iscritto senza gruppo.
//
// ⚠️ IL BOTTONE «Calendar» VIVE QUI (CALENDARIO CLINIC, Fetta 2, 23/9): questo
// è l'unico punto che disegna lo stato «iscritto», per «You» e per i figli,
// quindi il bottone compare sulle SOLE righe ENROLLED — mai su NOT ENROLLED né
// accanto a «Enrol». Va a capo sotto la pastiglia (`.qpsc-clinic__member-cal`,
// ClinicEvents.css).
// `idPersona` è l'id con cui la pagina conosce quella riga: la propria scheda
// per «You», l'id del figlio per un figlio. Senza id, nessun bottone.
//
// ⚠️ ACCANTO A «Calendar», IL BOTTONE «BOARD - CHAT» («BOARD - CHAT» Fetta A,
// 26/9/2026): la bacheca del gruppo della persona in QUESTO evento, su
// /clinics/board/:eventId/:personId (ClinicBoard.jsx). Solo con un gruppo
// (BAC-11): «no group yet» non ha bacheca. Il gruppo lo risolve il server.
function StatoIscritto({ gruppo, idPersona, apriCalendario, idEvento, apriBacheca }) {
  const nome = typeof gruppo === "string" ? gruppo.trim() : "";

  return (
    <span className="qpsc-clinic__member-state">
      <span className="qpsc-clinic__badge qpsc-clinic__badge--open">
        <FaCheck aria-hidden="true" /> ENROLLED
      </span>
      <span
        className={
          nome
            ? "qpsc-clinic__member-group"
            : "qpsc-clinic__member-group qpsc-clinic__member-group--none"
        }
      >
        {nome || "no group yet"}
      </span>
      {idPersona && apriCalendario && (
        <span className="qpsc-clinic__member-cal">
          <button
            type="button"
            className="qpsc-ghost-btn"
            onClick={() => apriCalendario(idPersona)}
          >
            <FaCalendarAlt aria-hidden="true" />
            <span>Calendar</span>
          </button>
          {nome && idEvento && apriBacheca && (
            <button
              type="button"
              className="qpsc-ghost-btn"
              onClick={() => apriBacheca(idEvento, idPersona)}
            >
              <FaComments aria-hidden="true" />
              <span>BOARD - CHAT</span>
            </button>
          )}
        </span>
      )}
    </span>
  );
}

export default function ClinicMember({ home = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [eventi, setEventi] = useState([]);
  const [figli, setFigli] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [erroreCaricamento, setErroreCaricamento] = useState(null);

  // QUALE ISCRIZIONE È IN VOLO — la chiave «evento|figlio», non un booleano,
  // perché il pulsante che l'ha avviata deve poterlo dire mentre gli altri si
  // limitano a spegnersi. Uno per volta.
  const [azioneInCorso, setAzioneInCorso] = useState(null);
  const [erroreAzione, setErroreAzione] = useState(null);

  // ⚠️ CHIAVE STABILE, non l'oggetto `user` (stessa avvertenza di HomeParent):
  // `refreshUserFromBackend` restituisce un oggetto NUOVO a ogni chiamata, e un
  // effetto che dipendesse da `user` rileggerebbe i figli a ogni giro.
  const idsFigli = useMemo(() => idsDeiFigli(user), [user]);

  // L'id della propria scheda: è la chiave con cui il server mette il proprio
  // gruppo in `enrolledGroups` (`childUserId` = id della scheda). E la riga
  // «You» si mostra a chi è PLAYER/COACH lui stesso — lo stesso metro dei figli —
  // oppure, su un evento, a chiunque vi risulti iscritto.
  const idSe = user && user.id ? String(user.id) : "";
  const ioIscrivibile = Boolean(user) && eIscrivibileAllaClinic(user);
  // (G2) L'adulto ospite: la sua riga «You» porta «Enrol».
  const ioOspiteAdulto = Boolean(user) && eOspiteAdulto(user);
  const chiaveFigli = idsFigli.join("|");

  // ⚠️ FINCHÉ `user` NON C'È NON SI LEGGE NULLA, e non è un'ottimizzazione.
  // AuthContext consegna l'utente in un secondo momento: senza questa riga la
  // pagina partiva subito, con zero figli, e mostrava per un istante «There is
  // no child linked to your account» a un genitore che di figli ne ha — poi si
  // correggeva da sola. Un messaggio che dice il falso, anche per un istante,
  // è peggio di «Loading…». (E per inciso risparmia la seconda lettura degli
  // eventi, che quella prima passata faceva a vuoto.)
  const utentePronto = Boolean(user);

  const caricaEventi = useCallback(async () => {
    const elenco = await api.get("/clinic-member/events");
    setEventi(Array.isArray(elenco) ? elenco : []);
  }, []);

  useEffect(() => {
    if (!utentePronto) return undefined;

    let vivo = true;

    const ids = chiaveFigli ? chiaveFigli.split("|") : [];

    (async () => {
      setCaricamento(true);
      setErroreCaricamento(null);

      try {
        // Le due letture insieme: gli eventi aperti e le schede dei figli. Un
        // figlio che risponde 403/404 non porta con sé gli altri — si scarta e
        // basta, come fa HomeParent con le sue tessere in errore: qui non c'è
        // nulla da mostrare per lui, e un errore di riga non deve impedire
        // l'iscrizione degli altri.
        const [, schede] = await Promise.all([
          caricaEventi(),
          Promise.all(
            ids.map((id) =>
              api
                .get(`/users/${encodeURIComponent(id)}`)
                .then((doc) => ({ id, doc }))
                .catch(() => ({ id, doc: null }))
            )
          ),
        ]);

        if (!vivo) return;

        // Si offrono i soli profili PLAYER/COACH (per ruolo, non per età): un
        // profilo collegato con un altro ruolo — o senza scheda leggibile —
        // non compare, perché il server lo rifiuterebbe.
        // (G2) Più i bambini OSPITI di un genitore ospite.
        setFigli(
          schede
            .filter(
              (riga) =>
                riga.doc && (eIscrivibileAllaClinic(riga.doc) || eOspiteMinore(riga.doc))
            )
            .map((riga) => ({ id: riga.id, nome: nomeDi(riga.doc) || riga.id }))
        );
      } catch (err) {
        if (!vivo) return;

        setErroreCaricamento(
          err && err.status === 403
            ? "You do not have access to clinic sessions."
            : "Clinic sessions could not be loaded. Reload the page; if it keeps failing, the server may be unreachable."
        );
        setEventi([]);
      } finally {
        if (vivo) setCaricamento(false);
      }
    })();

    return () => {
      vivo = false;
    };
  }, [caricaEventi, chiaveFigli, utentePronto]);

  // Il calendario di UNA persona (Fetta 2): l'id viaggia nell'indirizzo, come
  // in `/my-calendar/child/:childId`, così sopravvive al ricaricamento.
  const apriCalendario = (idPersona) =>
    navigate(`/clinics/calendar/${encodeURIComponent(idPersona)}`);

  // La bacheca del gruppo («BOARD - CHAT» Fetta A): evento e persona
  // nell'indirizzo, il gruppo lo risolve il server.
  const apriBacheca = (idEvento, idPersona) =>
    navigate(
      `/clinics/board/${encodeURIComponent(idEvento)}/${encodeURIComponent(idPersona)}`
    );

  // (Fetta C) La bacheca di un gruppo qualsiasi, dalla vista del coach: la
  // persona è la propria scheda, il gruppo viaggia in `?group=` e il server
  // decide se concederlo.
  const apriBachecaCoach = (idEvento, idGruppo) =>
    navigate(
      `/clinics/board/${encodeURIComponent(idEvento)}/${encodeURIComponent(idSe)}?group=${encodeURIComponent(idGruppo)}`
    );

  const iscrivi = async (evento, figlio) => {
    if (azioneInCorso) return;

    const chiave = `${evento.id}|${figlio.id}`;

    setAzioneInCorso(chiave);
    setErroreAzione(null);

    try {
      await api.post(`/clinic-member/events/${evento.id}/enrol`, {
        childId: figlio.id,
      });

      // Si aggiunge l'id all'evento appena toccato, senza rileggere tutto: la
      // risposta è l'iscrizione creata, e ciò che cambia a schermo è una sola
      // riga. Il conto vero resta quello del server, che si rilegge alla
      // prossima apertura della pagina.
      //
      // (G2) L'adulto ospite che iscrive sé stesso accende `selfEnrolled`, lo
      // stesso campo che il server manda per «You».
      setEventi((precedenti) =>
        precedenti.map((e) =>
          e.id === evento.id && idSe && figlio.id === idSe
            ? { ...e, selfEnrolled: true }
            : e.id === evento.id
            ? {
                ...e,
                enrolledChildIds: [
                  ...(Array.isArray(e.enrolledChildIds) ? e.enrolledChildIds : []),
                  figlio.id,
                ],
              }
            : e
        )
      );
    } catch (err) {
      setErroreAzione(spiegazioneDiUnErrore(err));
    } finally {
      setAzioneInCorso(null);
    }
  };

  return (
    // Con `home` (la home del guest, /home/guest) il Logout della barra resta
    // acceso: il guest non ha More, e questa è la sua unica pagina — un
    // vicolo cieco (cantiere LOGOUT-1, vedi UserBar.jsx). Lo decide la prop,
    // non il ruolo. Come pagina dei soci (/clinics) si esce da More.
    <HomeShell
      backTo={home ? undefined : () => navigate(-1)}
      showLogout={home}
    >
      <section className="qpsc-clinic">
        <header className="qpsc-clinic__head">
          {/* Stessa icona e stesso testo del pulsante che apre la pagina dalle
              home Player, Child e Coach (Fetta 2, 23/9). */}
          <div className="qpsc-clinic__title">
            <FaFutbol className="qpsc-clinic__title-icon" aria-hidden="true" />
            <h1 className="qpsc-page-title">Clinic / Off Season / Specials</h1>
          </div>
        </header>

        {erroreCaricamento && <p className="qpsc-clinic__error">{erroreCaricamento}</p>}

        {erroreAzione && (
          <p className="qpsc-clinic__error" role="alert">
            {erroreAzione}
          </p>
        )}

        <div className="qpsc-clinic__block">
          {caricamento && <p className="qpsc-clinic__hint">Loading…</p>}

          {!caricamento && !erroreCaricamento && eventi.length === 0 && (
            <p className="qpsc-clinic__hint">
              There are no clinic sessions open at the moment. They appear here when the
              club opens one.
            </p>
          )}

          {/* Il messaggio «You are not enrolled in any clinic session…» che
              stava qui è stato tolto su richiesta del committente (Fetta 2,
              23/9): le righe «You · NOT ENROLLED» dicono già lo stato. */}

          <ul className="qpsc-clinic__list">
            {eventi.map((evento) => {
              const iscritti = Array.isArray(evento.enrolledChildIds)
                ? evento.enrolledChildIds
                : [];
              const gruppi =
                evento.enrolledGroups && typeof evento.enrolledGroups === "object"
                  ? evento.enrolledGroups
                  : {};
              const ioIscritto = evento.selfEnrolled === true;
              // (Fetta C) Presenti solo se il server riconosce il coach.
              const gruppiCoach = Array.isArray(evento.coachGroups)
                ? evento.coachGroups.filter((g) => g && g.id && g.name)
                : [];
              const mostraIo = ioIscritto || ioIscrivibile || ioOspiteAdulto;
              const chiaveIo = `${evento.id}|${idSe}`;

              return (
                // Titolo intero + descrizione dell'admin, e basta: le DATE qui
                // non si mostrano più (decisione del committente, 16/9). Restano
                // nel dato dell'evento e nella plancia; al socio erano un muro di
                // pastiglie senza scopo. Il separatore fra un evento e l'altro è
                // il bordo della voce (--member, in ClinicEvents.css).
                <li key={evento.id} className="qpsc-clinic__item qpsc-clinic__item--member">
                  <div className="qpsc-clinic__detail">
                    <h2 className="qpsc-clinic__member-title">{evento.title}</h2>

                    {evento.description && (
                      <p className="qpsc-clinic__member-desc">{evento.description}</p>
                    )}

                    {(figli.length > 0 || mostraIo) && (
                      <ul className="qpsc-clinic__grouplist">
                        {/* Sé stessi: SOLO lo stato, mai «Enrol» (la POST lo
                            rifiuta: un adulto non iscrive sé stesso) — tranne
                            l'adulto ospite (G2), che dalla sua area si
                            iscrive da sé. */}
                        {mostraIo && (
                          <li key={`${evento.id}|io`} className="qpsc-clinic__group">
                            <span className="qpsc-clinic__group-name">You</span>

                            {ioIscritto ? (
                              <StatoIscritto
                                gruppo={gruppi[idSe]}
                                idPersona={idSe}
                                apriCalendario={apriCalendario}
                                idEvento={evento.id}
                                apriBacheca={apriBacheca}
                              />
                            ) : ioOspiteAdulto && idSe ? (
                              <button
                                type="button"
                                className="qpsc-ghost-btn"
                                onClick={() => iscrivi(evento, { id: idSe })}
                                disabled={azioneInCorso !== null}
                              >
                                <FaPlus aria-hidden="true" />
                                <span>
                                  {azioneInCorso === chiaveIo ? "Enrolling…" : "Enrol"}
                                </span>
                              </button>
                            ) : (
                              <span className="qpsc-clinic__badge qpsc-clinic__badge--closed">
                                NOT ENROLLED
                              </span>
                            )}
                          </li>
                        )}

                        {figli.map((figlio) => {
                          const gia = iscritti.includes(figlio.id);
                          const chiave = `${evento.id}|${figlio.id}`;

                          return (
                            <li key={chiave} className="qpsc-clinic__group">
                              <span className="qpsc-clinic__group-name">{figlio.nome}</span>

                              {gia ? (
                                <StatoIscritto
                                  gruppo={gruppi[figlio.id]}
                                  idPersona={figlio.id}
                                  apriCalendario={apriCalendario}
                                  idEvento={evento.id}
                                  apriBacheca={apriBacheca}
                                />
                              ) : (
                                <button
                                  type="button"
                                  className="qpsc-ghost-btn"
                                  onClick={() => iscrivi(evento, figlio)}
                                  disabled={azioneInCorso !== null}
                                >
                                  <FaPlus aria-hidden="true" />
                                  <span>
                                    {azioneInCorso === chiave ? "Enrolling…" : "Enrol"}
                                  </span>
                                </button>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}

                    {/* LA VISTA DEL COACH (Fetta C): tutti i gruppi dell'evento,
                        ognuno con la sua bacheca. */}
                    {gruppiCoach.length > 0 && idSe && (
                      <>
                        <p className="qpsc-clinic__hint">
                          Coach view · all group boards of this event
                        </p>
                        <ul className="qpsc-clinic__grouplist">
                          {gruppiCoach.map((g) => (
                            <li key={`${evento.id}|coach|${g.id}`} className="qpsc-clinic__group">
                              <span className="qpsc-clinic__group-name">{g.name}</span>
                              <button
                                type="button"
                                className="qpsc-ghost-btn"
                                onClick={() => apriBachecaCoach(evento.id, g.id)}
                              >
                                <FaComments aria-hidden="true" />
                                <span>BOARD - CHAT</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    </HomeShell>
  );
}
